import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    MenuItem,
    Stack
} from "@mui/material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import creditService from "../../services/creditService";

const ACTIVITY_TYPES = [
    "Competition",
    "Volunteering",
    "Research",
    "Project",
    "Startup",
    "Mentoring",
    "NSIIC Program",
    "Other"
];

export default function AssignCreditsPage() {
    const { userProfile } = useAuth();
    const [formData, setFormData] = useState({
        assignedTo: "",
        amount: "",
        activityType: "",
        description: "",
        isTeamCredit: false
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.assignedTo || !formData.amount || !formData.activityType) {
            setMessageType("error");
            setMessage("Please fill all required fields");
            return;
        }

        try {
            setLoading(true);
            setMessage("");

            await creditService.assignCredit({
                ...formData,
                amount: parseInt(formData.amount),
                assignedBy: userProfile?.uid
            });

            setMessageType("success");
            setMessage("Credits assigned successfully!");
            setFormData({
                assignedTo: "",
                amount: "",
                activityType: "",
                description: "",
                isTeamCredit: false
            });
        } catch (error) {
            setMessageType("error");
            setMessage(error.message || "Failed to assign credits");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminLayout title="Assign Credits">
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                    Assign Credits to Students
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Box component="form" onSubmit={handleSubmit} noValidate>
                                    {message && (
                                        <Alert severity={messageType} sx={{ mb: 2 }}>
                                            {message}
                                        </Alert>
                                    )}

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Student ID / User ID"
                                        name="assignedTo"
                                        value={formData.assignedTo}
                                        onChange={handleChange}
                                        required
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Credits Amount"
                                        name="amount"
                                        type="number"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        required
                                        inputProps={{ min: 1 }}
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        select
                                        label="Activity Type"
                                        name="activityType"
                                        value={formData.activityType}
                                        onChange={handleChange}
                                        required
                                    >
                                        {ACTIVITY_TYPES.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleChange}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="isTeamCredit"
                                                checked={formData.isTeamCredit}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Distribute to all team members"
                                        sx={{ my: 1 }}
                                    />

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        sx={{ mt: 2 }}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={22} /> : "Assign Credits"}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ backgroundColor: "info.lighter" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                    Activity Types
                                </Typography>
                                <Stack spacing={1}>
                                    {ACTIVITY_TYPES.map((type) => (
                                        <Typography key={type} variant="body2">
                                            • {type}
                                        </Typography>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </AdminLayout>
    );
}
